/*
  Copy a discount code to the clipboard when the shopper clicks a link that applies it.

  A `/discount/CODE?redirect=...` link already does the important half by itself: Shopify
  stores the code against the session and honours it when the bike is added, even when the
  cart was empty at the moment of the click. What it does NOT do is tell the shopper what
  the code was. If their session is cleared, or they come back on another device, or they
  simply want to check the discount is really there before paying, the code is gone - it was
  only ever in the URL they clicked. So the code goes to the clipboard on the way past.

  Bound to every /discount/ link rather than to one button, because the codes live on
  landing pages that get added over time and each new one would otherwise need wiring up.
  The handler is inert until such a link is clicked.

  Navigation is deferred briefly so the confirmation is actually readable - without it the
  page changes on the same tick and the shopper sees nothing. Every failure path still
  navigates: a refused clipboard permission must never cost the shopper their discount.

  A link marked `data-cb-no-copy` is left alone and navigates at once. The promo bar's
  offer link carries it (Olga, 2026-10-26): the code is already printed in the bar, and
  swapping "SHOP NOW." for "Code ... copied" read as the link not working.
*/
(function () {
  var CODE_IN_HREF = /\/discount\/([^/?#]+)/;
  var CONFIRM_MS = 750;

  function copy(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    // Safari and older browsers refuse writeText outside a narrow set of conditions.
    return new Promise(function (resolve, reject) {
      try {
        var field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.top = '-1000px';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        field.setSelectionRange(0, text.length);
        var ok = document.execCommand('copy');
        document.body.removeChild(field);
        if (ok) { resolve(); } else { reject(); }
      } catch (err) {
        reject(err);
      }
    });
  }

  function announce(message) {
    var region = document.getElementById('cb-discount-copy-status');
    if (!region) {
      region = document.createElement('p');
      region.id = 'cb-discount-copy-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.style.position = 'absolute';
      region.style.width = '1px';
      region.style.height = '1px';
      region.style.overflow = 'hidden';
      region.style.clip = 'rect(0 0 0 0)';
      region.style.whiteSpace = 'nowrap';
      document.body.appendChild(region);
    }
    region.textContent = message;
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.hasAttribute('data-cb-copying') || link.hasAttribute('data-cb-no-copy')) return;

    var match = CODE_IN_HREF.exec(link.getAttribute('href') || '');
    if (!match) return;

    var code = '';
    try { code = decodeURIComponent(match[1]); } catch (err) { code = match[1]; }
    if (!code) return;

    event.preventDefault();
    link.setAttribute('data-cb-copying', 'true');

    var destination = link.href;
    var label = link.querySelector('[data-cb-copy-label]') || link;
    var original = label.textContent;

    function go() {
      window.location.href = destination;
    }

    copy(code).then(
      function () {
        label.textContent = 'Code ' + code + ' copied';
        announce('Discount code ' + code + ' copied to your clipboard.');
        setTimeout(go, CONFIRM_MS);
      },
      function () {
        // The discount still applies through the link itself, so say nothing and move on.
        label.textContent = original;
        go();
      }
    );
  });
})();
